import React from 'react';
import cn from './primitives/cn';

/**
 * Publish status, as a labelled pill: a tint, a dot and the word.
 *
 * SURF-4808 step 2. It was a 16px coloured dot with the word hidden behind a hover
 * popover, so on every list screen the status column was three indistinguishable
 * circles and you had to point at each one to read it. Colour alone also carries
 * nothing for a colour-blind reader, and a hover target carries nothing on a tablet.
 *
 * Tables used a dot-only `compact` form for a while, to give the name column the width.
 * The redesign (iteration 1, 2026-09-29) puts the word back in every table, so there is
 * one form now and the column reads without knowing the colours. The dots are the three
 * publish-status colours the CMS has always drawn.
 *
 * `tone` is the visual state, not the domain state: several domain statuses map onto
 * one tone, which is why the mapping lives in the callers rather than here.
 */
const TONES = {
  ok: { dot: 'tw-bg-success-dot', pill: 'tw-bg-ok-bg tw-text-ok-ink' },
  warn: { dot: 'tw-bg-warning', pill: 'tw-bg-warn-bg tw-text-warn-ink' },
  bad: { dot: 'tw-bg-danger-strong', pill: 'tw-bg-bad-bg tw-text-bad-ink' },
  neutral: { dot: 'tw-bg-subtle', pill: 'tw-bg-fill tw-text-ink-2' },
};

const StatusPill = ({ tone = 'neutral', children, className, ...rest }) => {
  const { dot, pill } = TONES[tone] || TONES.neutral;
  return (
    <span
      className={cn(
        'tw-inline-flex tw-items-center tw-gap-1.5 tw-h-pill tw-rounded-pill',
        // The dot side is 2px tighter, so the dot and the word read as centred.
        'tw-pl-2 tw-pr-2.5 tw-font-sans tw-text-caption tw-font-semibold tw-whitespace-nowrap',
        pill,
        className
      )}
      {...rest}
    >
      <span
        className={cn('tw-h-dot tw-w-dot tw-rounded-pill tw-shrink-0', dot)}
        aria-hidden="true"
      />
      {children}
    </span>
  );
};

export default StatusPill;
